import type { OfflineStore } from './store'
import { SessionExpiredError, type EntryBatchTransport, type EntryMutation, type EntryMutationInput, type MutationAcknowledgement, type SyncSnapshot } from './types'

type Listener = (snapshot: SyncSnapshot) => void
const BATCH_SIZE = 100
const outcomes = new Set(['T', 'W', 'C', 'B', null])

function validAcknowledgement(ack: MutationAcknowledgement, queued: EntryMutation) {
  return ack.id === queued.id &&
    ack.cellKey === queued.cellKey &&
    ack.outcome === queued.outcome &&
    ack.deleted === queued.deleted &&
    outcomes.has(ack.outcome) &&
    typeof ack.deleted === 'boolean' &&
    (!ack.deleted || ack.outcome === null) &&
    Number.isFinite(Date.parse(ack.serverReceivedAt)) &&
    ack.resolution.cellKey === queued.cellKey &&
    outcomes.has(ack.resolution.outcome) &&
    typeof ack.resolution.deleted === 'boolean' &&
    (!ack.resolution.deleted || ack.resolution.outcome === null) &&
    Number.isFinite(Date.parse(ack.resolution.serverReceivedAt))
}

export class SyncEngine {
  private snapshot: SyncSnapshot = { phase: 'synced', pending: 0 }
  private listeners = new Set<Listener>()
  private online = true
  private authPaused = false
  private flushing?: Promise<void>

  constructor(private readonly store: OfflineStore, private readonly transport: EntryBatchTransport) {}

  subscribe(listener: Listener) {
    this.listeners.add(listener)
    listener(this.snapshot)
    return () => this.listeners.delete(listener)
  }

  getSnapshot() { return this.snapshot }

  async initialize(online: boolean) {
    this.online = online
    const pending = await this.store.pendingCount()
    this.publish({ phase: online ? (pending ? 'saving' : 'synced') : 'offline', pending })
    if (online && pending) await this.flush()
  }

  async enqueue(input: EntryMutationInput) {
    const result = await this.store.enqueue(input)
    const pending = await this.store.pendingCount()
    this.publish({ phase: this.online ? 'saving' : 'offline', pending, message: result.duplicate ? 'Duplicate replay suppressed.' : undefined })
    if (this.online && !this.authPaused) void this.flush()
    return result
  }

  setOnline(online: boolean) {
    this.online = online
    if (!online) {
      this.publish({ ...this.snapshot, phase: 'offline', message: this.snapshot.pending ? 'Changes are safely queued on this device.' : 'You are offline.' })
      return
    }
    if (this.authPaused) {
      this.publish({ ...this.snapshot, phase: 'auth-paused', message: 'Sign in again to resume sync.' })
      return
    }
    void this.flush()
  }

  resumeAfterAuthentication() {
    this.authPaused = false
    if (this.online) void this.flush()
  }

  async flush() {
    if (this.flushing) return this.flushing
    this.flushing = this.flushInternal().finally(() => { this.flushing = undefined })
    return this.flushing
  }

  private async flushInternal() {
    if (!this.online || this.authPaused) return
    let pending = await this.store.listPending()
    if (!pending.length) {
      this.publish({ phase: 'synced', pending: 0, lastSyncedAt: this.snapshot.lastSyncedAt })
      return
    }
    this.publish({ phase: 'saving', pending: pending.length })
    while (pending.length && this.online && !this.authPaused) {
      const batch = pending.slice(0, BATCH_SIZE)
      const ids = batch.map(({ id }) => id)
      await this.store.markSending(ids, new Date().toISOString())
      try {
        const response = await this.transport.send(batch)
        const queuedById = new Map(batch.map((mutation) => [mutation.id, mutation]))
        const acknowledgements = response.filter((ack) => {
          const queued = queuedById.get(ack.id)
          return queued ? validAcknowledgement(ack, queued) : false
        })
        const acknowledged = new Set(acknowledgements.map(({ id }) => id))
        await this.store.acknowledge(acknowledgements)
        const unacknowledged = ids.filter((id) => !acknowledged.has(id))
        if (unacknowledged.length) {
          await this.store.markQueued(unacknowledged, 'invalid_or_missing_acknowledgement')
          this.publish({ phase: 'error', pending: await this.store.pendingCount(), message: 'The server did not safely acknowledge every queued change.' })
          return
        }
      } catch (error) {
        if (error instanceof SessionExpiredError) {
          this.authPaused = true
          await this.store.markQueued(ids, 'session_expired')
          this.publish({ phase: 'auth-paused', pending: await this.store.pendingCount(), message: error.message })
          return
        }
        await this.store.markQueued(ids, 'retryable_error')
        this.publish({ phase: 'error', pending: await this.store.pendingCount(), message: 'Sync paused after a network error. Your queued changes are safe.' })
        return
      }
      pending = await this.store.listPending()
    }
    const remaining = await this.store.pendingCount()
    this.publish({ phase: remaining ? (this.online ? 'error' : 'offline') : 'synced', pending: remaining, lastSyncedAt: new Date().toISOString(), message: remaining ? 'Queued changes remain on this device.' : undefined })
  }

  private publish(snapshot: SyncSnapshot) {
    this.snapshot = snapshot
    for (const listener of this.listeners) listener(snapshot)
  }
}
